import React, { useState, useEffect, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Star, MessageSquare, X, Download } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import api from '../../api';

const COLORS = ['#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7'];

const LMSFeedbackAnalytics = ({ courseId, courseName, universityName, displayType = 'both' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeModalQuestion, setActiveModalQuestion] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingBatch, setDownloadingBatch] = useState(null);
  const [expandedBatches, setExpandedBatches] = useState({});
  const reportRef = useRef(null);

  const toggleBatch = (batchName) => {
    setExpandedBatches(prev => ({ ...prev, [batchName]: !prev[batchName] }));
  };

  const downloadReport = async (specificBatch = null) => {
    if (!reportRef.current) return;
    try {
      setDownloading(true);
      setDownloadingBatch(specificBatch);
      // Wait for React to render (hiding other batches if specific, rendering appendix)
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const uniStr = universityName && universityName !== 'All' ? `_${universityName.replace(/\s+/g, '_')}` : '';
      const courseStr = courseId && courseId !== 'All' ? `_${courseId}` : '_All';
      const batchStr = specificBatch ? `_${specificBatch.replace(/\s+/g, '_')}` : '';
      const filename = `LMS_Analytics_Report${uniStr}${courseStr}${batchStr}.pdf`;

      if (!window.html2pdf) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }

      const opt = {
        margin:       10,
        filename:     filename,
        image:        { type: 'jpeg', quality: 0.98 },
        html2canvas:  { scale: 2, useCORS: true, logging: false, scrollY: 0, scrollX: 0 },
        jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak:    { mode: ['avoid-all', 'css', 'legacy'] }
      };

      await window.html2pdf().set(opt).from(reportRef.current).save();

    } catch (err) {
      console.error("Error generating PDF", err);
      alert("Failed to generate PDF report.");
    } finally {
      setDownloading(false);
      setDownloadingBatch(null);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [courseId, universityName]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      let url = '/admin/lms/feedback/analytics?';
      if (courseId && courseId !== 'All') url += `course_id=${courseId}&`;
      if (universityName && universityName !== 'All') url += `university_name=${encodeURIComponent(universityName)}`;
      const res = await api.get(url);
      setData(res.data);
      // Expand all by default initially
      if (res.data) {
        const initialExpanded = {};
        Object.keys(res.data).forEach(batch => initialExpanded[batch] = true);
        setExpandedBatches(initialExpanded);
      }
    } catch (err) {
      console.error("Error fetching feedback analytics", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>Loading analytics...</div>;
  }

  if (!data || Object.keys(data).length === 0) {
    return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>No feedback data available for analytics.</div>;
  }

  const renderRating = (q) => {
    const dist = q.distribution || {};
    const total = q.total || 1;
    
    return (
      <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
        <div style={{ textAlign: 'center', minWidth: '100px' }}>
          <div style={{ fontSize: '2.5rem', fontWeight: 800, color: 'var(--text-main)', lineHeight: 1 }}>{q.average}</div>
          <div style={{ color: 'var(--primary)', display: 'flex', justifyContent: 'center', margin: '4px 0' }}>
            <Star fill="currentColor" size={20} />
          </div>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{q.total} responses</div>
        </div>
        
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {[5, 4, 3, 2, 1].map(star => {
            const count = dist[star] || 0;
            const percentage = total > 0 ? (count / total) * 100 : 0;
            return (
              <div key={star} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.85rem' }}>
                <span style={{ width: '12px', color: 'var(--text-muted)' }}>{star}</span>
                <Star size={12} fill="var(--text-muted)" color="var(--text-muted)" />
                <div style={{ flex: 1, height: '8px', background: 'var(--bg-main)', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ width: `${percentage}%`, height: '100%', background: 'var(--primary)', borderRadius: '4px' }} />
                </div>
                <span style={{ width: '30px', textAlign: 'right', color: 'var(--text-muted)' }}>{count}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderChoice = (q) => {
    const counts = q.counts || {};
    const chartData = Object.keys(counts).map(key => ({
      name: key,
      value: counts[key]
    }));

    return (
      <div style={{ height: '200px', width: '100%', marginTop: '10px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <XAxis type="number" hide />
            <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 12, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
            <Tooltip cursor={{ fill: 'var(--bg-main)' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
            <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={20}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderText = (q) => {
    const comments = q.all_comments || q.latest_comments || [];
    if (comments.length === 0) return <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>No responses yet.</p>;

    const limit = 3;
    
    if (downloading) {
      return (
        <div style={{ marginTop: '15px', padding: '15px', background: 'rgba(25, 211, 255, 0.05)', borderRadius: '8px', borderLeft: '4px solid var(--primary)' }}>
          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.9rem', fontStyle: 'italic' }}>
            Open text responses for this question are detailed in the <strong>Open Text Responses</strong> section at the bottom of this batch.
          </p>
        </div>
      );
    }

    const displayedComments = comments.slice(0, limit);
    const hasMore = comments.length > limit;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
        {displayedComments.map((comment, i) => (
          <div key={i} style={{ background: 'var(--bg-main)', padding: '12px 16px', borderRadius: '8px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <MessageSquare size={16} color="var(--primary)" style={{ marginTop: '3px', flexShrink: 0 }} />
            <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-main)', lineHeight: '1.5' }}>"{comment}"</p>
          </div>
        ))}
        {hasMore && (
          <button
            onClick={() => setActiveModalQuestion(q)}
            style={{
              marginTop: '8px',
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid var(--primary)',
              background: 'transparent',
              color: 'var(--primary)',
              fontSize: '0.85rem',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              width: 'fit-content'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'rgba(25, 211, 255, 0.1)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            View All ({comments.length})
          </button>
        )}
      </div>
    );
  };

  const renderSection = (sectionData, title) => {
    const keys = Object.keys(sectionData);
    if (keys.length === 0) return null;

    return (
      <div style={{ marginBottom: '40px' }}>
        <h3 style={{ fontSize: '1.2rem', color: 'var(--text-main)', marginBottom: '20px', paddingBottom: '10px', borderBottom: '2px solid var(--bg-main)' }}>{title}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px' }}>
          {keys.map(qId => {
            const q = sectionData[qId];
            return (
              <div key={qId} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <h4 style={{ margin: '0 0 20px 0', fontSize: '1rem', color: 'var(--text-main)', lineHeight: '1.4' }}>{q.question}</h4>
                {q.type === 'rating' && renderRating(q)}
                {q.type === 'choice' && renderChoice(q)}
                {q.type === 'text' && renderText(q)}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderBatchAppendix = (batchName, batchData) => {
    let questions = [];
    const collectText = (sectionData) => {
      if (!sectionData) return;
      Object.keys(sectionData).forEach(q_id => {
        const qData = sectionData[q_id];
        if (qData.type === 'text' && qData.all_comments && qData.all_comments.length > 0) {
          questions.push({ question: qData.question, answers: qData.detailed_answers || [] });
        }
      });
    };

    if (displayType === 'both' || displayType === 'lms') {
      collectText(batchData.module_feedback);
    }
    if (displayType === 'both' || displayType === 'platform') {
      collectText(batchData.platform_feedback);
    }

    if (questions.length === 0) return null;

    return (
      <div style={{ marginTop: '30px', borderTop: '2px dashed var(--border)', paddingTop: '30px' }} className="pdf-export-appendix">
        <h3 style={{ color: 'var(--primary)', fontSize: '1.2rem', margin: '0 0 20px 0', paddingBottom: '10px', borderBottom: '2px solid var(--bg-main)' }}>
          Open Text Responses
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '24px' }}>
          {questions.map((item, qIdx) => (
            <div key={qIdx}>
              <h4 style={{ color: 'var(--text-main)', fontSize: '1.05rem', margin: '0 0 16px 0', lineHeight: 1.4 }}>{item.question}</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }}>
                {item.answers.map((ansObj, aIdx) => (
                  <div key={aIdx} style={{ background: 'var(--bg-main)', padding: '16px', borderRadius: '8px', borderLeft: '4px solid var(--primary)' }}>
                    <div style={{ fontSize: '0.95rem', color: 'var(--text-main)', lineHeight: 1.6 }}>
                      "{typeof ansObj === 'string' ? ansObj : ansObj.answer}"
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
        <button 
          onClick={() => downloadReport(null)}
          disabled={downloading}
          style={{ 
            display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', 
            background: '#10b981', color: '#fff', border: 'none', borderRadius: '8px', 
            fontSize: '0.9rem', fontWeight: 600, cursor: downloading ? 'not-allowed' : 'pointer', transition: 'all 0.2s' 
          }}
          onMouseOver={(e) => {
            if (!downloading) e.currentTarget.style.opacity = '0.9';
          }}
          onMouseOut={(e) => {
            if (!downloading) e.currentTarget.style.opacity = '1';
          }}
        >
          <Download size={18} />
          {downloading && !downloadingBatch ? 'Generating PDF...' : 'Download All Batches Report'}
        </button>
      </div>

      <div ref={reportRef} style={{ background: 'var(--bg-main)', padding: '30px', borderRadius: '12px' }}>
        <h2 style={{ margin: '0 0 30px 0', color: 'var(--text-main)', borderBottom: '2px solid var(--border)', paddingBottom: '16px' }}>
          {displayType === 'platform' ? 'Platform Feedback Analytics Report' : 'LMS Course Feedback Analytics Report'}
          {universityName && universityName !== 'All' ? ` - ${universityName}` : ''}
          {courseName && courseId !== 'All' ? ` - ${courseName}` : (courseId && courseId !== 'All' ? ` - ${courseId}` : '')}
          {downloadingBatch ? ` (Batch: ${downloadingBatch})` : ''}
        </h2>
        {Object.entries(data).map(([batchName, batchData]) => {
          const hasLMS = batchData.module_feedback && Object.keys(batchData.module_feedback).length > 0;
          const hasPlatform = batchData.platform_feedback && Object.keys(batchData.platform_feedback).length > 0;
          
          if (displayType === 'lms' && !hasLMS) return null;
          if (displayType === 'platform' && !hasPlatform) return null;
          if (!hasLMS && !hasPlatform) return null;

          // If we are downloading a specific batch, hide the others
          if (downloading && downloadingBatch && downloadingBatch !== batchName) {
            return null;
          }
          
          const isExpanded = expandedBatches[batchName] || downloading; // always expand during download

          return (
            <div key={batchName} style={{ marginBottom: '40px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
              <div 
                onClick={() => !downloading && toggleBatch(batchName)}
                style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  padding: '16px 24px', 
                  background: 'var(--bg-main)', 
                  cursor: downloading ? 'default' : 'pointer',
                  borderBottom: isExpanded ? '1px solid var(--border)' : 'none'
                }}
              >
                <h3 style={{ fontSize: '1.1rem', color: 'var(--primary)', margin: 0 }}>
                  Batch: {batchName}
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  {!downloading && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadReport(batchName);
                      }}
                      style={{
                        background: 'var(--bg-card)',
                        border: '1px solid var(--border)',
                        color: 'var(--text-main)',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontSize: '0.8rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.borderColor = 'var(--primary)'}
                      onMouseOut={(e) => e.currentTarget.style.borderColor = 'var(--border)'}
                    >
                      <Download size={14} />
                      {downloading && downloadingBatch === batchName ? 'Exporting...' : 'Export Batch Report'}
                    </button>
                  )}
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                    {isExpanded ? '▼ Hide Analytics' : '▶ Show Analytics'}
                  </div>
                </div>
              </div>
              
              {isExpanded && (
                <div style={{ padding: '24px' }}>
                  {(displayType === 'both' || displayType === 'lms') && renderSection(batchData.module_feedback, "Course Module Feedback Insights")}
                  {(displayType === 'both' || displayType === 'platform') && renderSection(batchData.platform_feedback, "Platform Feedback Insights")}
                  {downloading && renderBatchAppendix(batchName, batchData)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {activeModalQuestion && (
        <div 
          className="modal-overlay" 
          onClick={() => setActiveModalQuestion(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(11, 15, 20, 0.85)',
            backdropFilter: 'blur(12px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '24px'
          }}
        >
          <div 
            className="modal-box" 
            onClick={e => e.stopPropagation()} 
            style={{ 
              maxWidth: '650px', 
              width: '100%', 
              maxHeight: '80vh', 
              display: 'flex', 
              flexDirection: 'column',
              padding: '24px',
              borderRadius: '16px',
              border: '1px solid var(--border)',
              background: 'var(--bg-card)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', gap: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '1.2rem', color: 'var(--text-main)', fontWeight: 600, lineHeight: 1.4 }}>
                {activeModalQuestion.question}
              </h3>
              <button 
                onClick={() => setActiveModalQuestion(null)}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  cursor: 'pointer', 
                  color: 'var(--text-muted)',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.color = 'var(--text-main)'}
                onMouseOut={(e) => e.currentTarget.style.color = 'var(--text-muted)'}
              >
                <X size={20} />
              </button>
            </div>
            
            <div 
              className="custom-scrollbar"
              style={{ 
                flex: 1, 
                overflowY: 'auto', 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '12px',
                paddingRight: '4px'
              }}
            >
              {(activeModalQuestion.all_comments || activeModalQuestion.latest_comments || []).map((comment, i) => (
                <div key={i} style={{ background: 'var(--bg-main)', padding: '16px', borderRadius: '8px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <MessageSquare size={16} color="var(--primary)" style={{ marginTop: '3px', flexShrink: 0 }} />
                  <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-main)', lineHeight: '1.5' }}>"{comment}"</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LMSFeedbackAnalytics;
